import { Component } from '@theme/component';

/**
 * Collapses a block of rich text to a fixed number of lines and reveals the rest on demand.
 *
 * The full text is always present in the DOM — collapsing is done purely in CSS via
 * `line-clamp`, so search engines and screen readers that ignore the visual state still
 * read every word. Nothing is clamped and no toggle is shown until this component
 * confirms the text actually overflows, so short copy is left alone and the section
 * degrades to plain, fully visible text when JavaScript is unavailable.
 *
 * @extends {Component<{ content: HTMLElement, toggle: HTMLButtonElement, toggleLabel: HTMLElement }>}
 */
class ExpandableTextComponent extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #lastWidth;

  connectedCallback() {
    super.connectedCallback();

    /*
     * Observing width only. Toggling the collapsed state changes the element's
     * height, so watching height here would let the component retrigger itself.
     * The observer fires once on observe(), which doubles as the initial measure.
     */
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width === this.#lastWidth) return;
      this.#lastWidth = width;
      this.#syncCollapsibility();
    });

    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Toggles between the collapsed and expanded states.
   */
  toggle() {
    this.expanded = !this.expanded;
  }

  get expanded() {
    return this.hasAttribute('expanded');
  }

  set expanded(value) {
    this.toggleAttribute('expanded', value);

    const { toggle, toggleLabel } = this.refs;
    toggle.setAttribute('aria-expanded', String(value));

    if (toggleLabel instanceof HTMLElement) {
      const label = value ? this.dataset.collapseLabel : this.dataset.expandLabel;
      if (label) toggleLabel.textContent = label;
    }
  }

  /**
   * Decides whether the text is long enough to be worth collapsing, and shows or
   * hides the toggle to match.
   *
   * The measurement is taken in the collapsed state on purpose: `line-clamp` leaves
   * the full text in `scrollHeight` while `clientHeight` reports the clamped box, so
   * the two only differ when there is genuinely more copy than fits.
   */
  #syncCollapsibility() {
    const { content, toggle } = this.refs;
    if (!(content instanceof HTMLElement)) return;

    const wasExpanded = this.expanded;

    this.setAttribute('collapsible', '');
    this.removeAttribute('expanded');

    const overflows = content.scrollHeight > content.clientHeight + 1;

    this.toggleAttribute('collapsible', overflows);
    toggle.hidden = !overflows;

    if (overflows) this.expanded = wasExpanded;
  }
}

if (!customElements.get('expandable-text')) {
  customElements.define('expandable-text', ExpandableTextComponent);
}
